import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, User, ArrowRight, Sun, Moon, Mail, Phone, Building2, MapPin, Globe, Store, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  'Restaurant',
  'Cafe',
  'Retail Store',
  'Hotel',
  'Salon & Spa',
  'Gym & Fitness',
  'Office',
  'Shopping Mall',
  'Hospital & Clinic',
  'Supermarket',
  'Other'
];

const Auth = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    business_name: '',
    city: '',
    country: '',
    phone: '',
    num_stores: '',
    business_type: ''
  });
  const { theme, toggleTheme, isLight } = useTheme();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: formData.username,
        password: formData.password
      });
      const { access_token, user } = response.data;

      toast.success('Welcome back!');
      onLogin(access_token, user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register-interest`, registerData);
      toast.success('Thank you! We will contact you soon.');
      setIsRegistering(false);
      setRegisterData({
        name: '',
        email: '',
        business_name: '',
        city: '',
        country: '',
        phone: '',
        num_stores: '',
        business_type: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex relative overflow-hidden ${isLight ? 'bg-[#faf8f5]' : 'bg-[#0a0a0f]'}`}>
      {/* Theme Toggle - Top Right */}
      <button
        onClick={toggleTheme}
        className={`absolute top-6 right-6 z-50 p-3 rounded-xl transition-all ${
          isLight 
            ? 'bg-white border border-[#ebe7e0] text-gray-600 hover:text-[#e94560] shadow-sm' 
            : 'bg-white/5 border border-white/10 text-slate-400 hover:text-white'
        }`}
      >
        {isLight ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
      </button>

      {/* Left side - Hero with Large Logo */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        {/* Background - matches logo red */}
        <div className={`absolute inset-0 ${
          isLight 
            ? 'bg-[#E5253B]' 
            : 'bg-gradient-to-br from-[#E5253B] via-[#b91c2e] to-[#0a0a0f]'
        }`} />
        
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-16">
          {/* Large Logo */}
          <div className="mb-8">
            <img 
              src="/redmlabs-logo.png"
              alt="Redmlabs"
              className="h-64 w-auto object-contain drop-shadow-2xl"
            />
          </div>
          
          {/* Tagline */}
          <h1 className="text-3xl lg:text-4xl font-bold text-center mb-4 leading-tight text-white">
            Professional Music
            <br />
            <span className="text-white/90">Streaming for Business</span>
          </h1>
          <p className="text-lg text-center max-w-md text-white/70">
            Curate the perfect ambiance for your business with scheduled playlists,
            custom announcements, and seamless music management.
          </p>
          
          {/* Feature Pills */}
          <div className="flex flex-wrap gap-3 mt-8 justify-center">
            {['Scheduled Playlists', 'Custom Announcements', 'Multi-Store', 'Analytics'].map((feature) => (
              <span 
                key={feature}
                className="px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white/90 backdrop-blur-sm border border-white/20"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img 
              src="/redmlabs-logo.png"
              alt="Redmlabs"
              className="h-24 w-auto object-contain"
            />
          </div>

          {!isRegistering ? (
            /* Login Form */
            <div className={`rounded-2xl p-8 ${
              isLight 
                ? 'bg-white border border-[#ebe7e0] shadow-xl' 
                : 'bg-white/5 border border-white/10 backdrop-blur-xl'
            }`}>
              <div className="text-center mb-8">
                <h2 className={`text-2xl font-bold mb-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>
                  Welcome Back
                </h2>
                <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>
                  Sign in to manage your music experience
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>Username</Label>
                  <div className="relative mt-1.5">
                    <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                    <Input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className={`pl-10 h-12 ${
                        isLight 
                          ? 'bg-[#faf8f5] border-[#ebe7e0] focus:border-red-500' 
                          : 'bg-white/5 border-white/10 text-white focus:border-red-500'
                      }`}
                      placeholder="Enter your username"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>Password</Label>
                  <div className="relative mt-1.5">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`pl-10 h-12 ${
                        isLight 
                          ? 'bg-[#faf8f5] border-[#ebe7e0] focus:border-red-500' 
                          : 'bg-white/5 border-white/10 text-white focus:border-red-500'
                      }`}
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl transition-all"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>
                  New to Redmlabs?{' '}
                  <button 
                    onClick={() => setIsRegistering(true)}
                    className="text-red-500 hover:text-red-600 font-semibold"
                  >
                    Get Started
                  </button>
                </p>
              </div>
            </div>
          ) : (
            /* Registration Form */
            <div className={`rounded-2xl p-8 ${
              isLight 
                ? 'bg-white border border-[#ebe7e0] shadow-xl' 
                : 'bg-white/5 border border-white/10 backdrop-blur-xl'
            }`}>
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold mb-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>
                  Get Started
                </h2>
                <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>
                  Tell us about your business
                </p>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>Your Name *</Label>
                    <div className="relative mt-1">
                      <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                      <Input
                        type="text"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        className={`pl-9 h-10 text-sm ${
                          isLight 
                            ? 'bg-[#faf8f5] border-[#ebe7e0]' 
                            : 'bg-white/5 border-white/10 text-white'
                        }`}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>Email *</Label>
                    <div className="relative mt-1">
                      <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                      <Input
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className={`pl-9 h-10 text-sm ${
                          isLight 
                            ? 'bg-[#faf8f5] border-[#ebe7e0]' 
                            : 'bg-white/5 border-white/10 text-white'
                        }`}
                        placeholder="john@company.com"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>Business Name *</Label>
                  <div className="relative mt-1">
                    <Building2 className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                    <Input
                      type="text"
                      value={registerData.business_name}
                      onChange={(e) => setRegisterData({ ...registerData, business_name: e.target.value })}
                      className={`pl-9 h-10 text-sm ${
                        isLight 
                          ? 'bg-[#faf8f5] border-[#ebe7e0]' 
                          : 'bg-white/5 border-white/10 text-white'
                      }`}
                      placeholder="Your Business Name"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>City *</Label>
                    <div className="relative mt-1">
                      <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                      <Input
                        type="text"
                        value={registerData.city}
                        onChange={(e) => setRegisterData({ ...registerData, city: e.target.value })}
                        className={`pl-9 h-10 text-sm ${
                          isLight 
                            ? 'bg-[#faf8f5] border-[#ebe7e0]' 
                            : 'bg-white/5 border-white/10 text-white'
                        }`}
                        placeholder="Mumbai"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>Country *</Label>
                    <div className="relative mt-1">
                      <Globe className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                      <Input
                        type="text"
                        value={registerData.country}
                        onChange={(e) => setRegisterData({ ...registerData, country: e.target.value })}
                        className={`pl-9 h-10 text-sm ${
                          isLight 
                            ? 'bg-[#faf8f5] border-[#ebe7e0]' 
                            : 'bg-white/5 border-white/10 text-white'
                        }`}
                        placeholder="India"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>Phone Number *</Label>
                    <div className="relative mt-1">
                      <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                      <Input
                        type="tel"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        className={`pl-9 h-10 text-sm ${
                          isLight 
                            ? 'bg-[#faf8f5] border-[#ebe7e0]' 
                            : 'bg-white/5 border-white/10 text-white'
                        }`}
                        placeholder="+91 98765 43210"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>Number of Stores *</Label>
                    <div className="relative mt-1">
                      <Store className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                      <Input
                        type="number"
                        min="1"
                        value={registerData.num_stores}
                        onChange={(e) => setRegisterData({ ...registerData, num_stores: e.target.value })}
                        className={`pl-9 h-10 text-sm ${
                          isLight 
                            ? 'bg-[#faf8f5] border-[#ebe7e0]' 
                            : 'bg-white/5 border-white/10 text-white'
                        }`}
                        placeholder="1"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className={isLight ? 'text-gray-600' : 'text-slate-300'}>Nature of Business *</Label>
                  <Select
                    value={registerData.business_type}
                    onValueChange={(value) => setRegisterData({ ...registerData, business_type: value })}
                    required
                  >
                    <SelectTrigger className={`mt-1 h-10 ${
                      isLight 
                        ? 'bg-[#faf8f5] border-[#ebe7e0]' 
                        : 'bg-white/5 border-white/10 text-white'
                    }`}>
                      <Briefcase className={`w-4 h-4 mr-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                      <SelectValue placeholder="Select business type" />
                    </SelectTrigger>
                    <SelectContent className={isLight ? 'bg-white' : 'bg-slate-900 border-white/10'}>
                      {BUSINESS_TYPES.map((type) => (
                        <SelectItem key={type} value={type} className={isLight ? '' : 'text-white'}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !registerData.business_type}
                  className="w-full h-12 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl transition-all mt-2"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>
                  Already have an account?{' '}
                  <button 
                    onClick={() => setIsRegistering(false)}
                    className="text-red-500 hover:text-red-600 font-semibold"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
